import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowRight, ClipboardCheck, Clock } from 'lucide-react';
import { api, ApiError } from '../api/cliente.js';
import { Botao } from '../componentes/ui/Botao.js';
import { Modal } from '../componentes/ui/Modal.js';
import { Pill, type TomPill } from '../componentes/ui/Pill.js';

interface Solicitacao {
  id: string;
  ip: string;
  zona: string;
  ptrAntigo: string | null;
  ptrNovo: string;
  status: 'PENDENTE' | 'APROVADA' | 'REJEITADA' | 'PUBLICADA';
  motivoRejeicao: string | null;
  criadoEm: string;
  revisadoEm: string | null;
  usuario: { nome: string; email: string };
  revisor: { nome: string; email: string } | null;
  bloco: { id: string; cidr: string; operadora: { nome: string } } | null;
}

const STATUS: Record<Solicitacao['status'], { rotulo: string; tom: TomPill }> = {
  PENDENTE: { rotulo: 'Aguardando', tom: 'aviso' },
  PUBLICADA: { rotulo: 'Publicada', tom: 'ok' },
  APROVADA: { rotulo: 'Aprovada', tom: 'ok' },
  REJEITADA: { rotulo: 'Rejeitada', tom: 'erro' },
};

export function Solicitacoes() {
  const qc = useQueryClient();
  const [rejeitando, setRejeitando] = useState<Solicitacao | null>(null);

  const { data: solicitacoes = [], isLoading } = useQuery({
    queryKey: ['solicitacoes'],
    queryFn: () => api.get<Solicitacao[]>('/solicitacoes'),
    // Pedidos chegam do portal a qualquer momento; sem polling o operador
    // precisaria recarregar a página para descobrir que há trabalho.
    refetchInterval: 60_000,
  });

  const recarregar = () => void qc.invalidateQueries({ queryKey: ['solicitacoes'] });

  const pendentes = solicitacoes.filter((s) => s.status === 'PENDENTE');
  const resolvidas = solicitacoes.filter((s) => s.status !== 'PENDENTE');

  if (isLoading) return <Esqueleto />;

  return (
    <div className="space-y-6">
      <section>
        <header className="mb-3 flex items-center gap-2">
          <h2 className="text-[15px] font-extrabold">Aguardando revisão</h2>
          {pendentes.length > 0 && (
            <span className="rounded-pill bg-acento px-2 py-0.5 text-[11.5px] font-extrabold text-white">
              {pendentes.length}
            </span>
          )}
        </header>

        {pendentes.length === 0 ? (
          <div className="rounded-card border border-borda bg-superficie px-6 py-12 text-center">
            <ClipboardCheck size={24} className="mx-auto mb-2 text-texto-mudo" />
            <p className="text-[14px] font-bold">Nada aguardando</p>
            <p className="mt-1 text-[13px] text-texto-suave">
              Alterações pedidas pelo portal do cliente aparecem aqui quando o bloco exige
              aprovação.
            </p>
          </div>
        ) : (
          <div className="grid gap-4 lg:grid-cols-2">
            {pendentes.map((s) => (
              <CartaoPendente
                key={s.id}
                solicitacao={s}
                aoResolver={recarregar}
                aoRejeitar={() => setRejeitando(s)}
              />
            ))}
          </div>
        )}
      </section>

      {resolvidas.length > 0 && (
        <section>
          <h2 className="mb-3 text-[15px] font-extrabold">Histórico</h2>
          <div className="overflow-hidden rounded-card border border-borda bg-superficie">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[760px] border-collapse">
                <thead>
                  <tr className="border-b border-borda bg-borda-tenue">
                    {['Endereço', 'Alteração', 'Solicitante', 'Revisor', 'Situação'].map((h) => (
                      <th
                        key={h}
                        className="px-[18px] py-3 text-left text-[11px] font-bold uppercase tracking-[0.6px] text-texto-suave"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {resolvidas.map((s) => (
                    <tr key={s.id} className="border-t border-borda-suave">
                      <td className="px-[18px] py-[13px] font-mono text-[13px]">{s.ip}</td>
                      <td className="px-[18px] py-[13px]">
                        <Alteracao de={s.ptrAntigo} para={s.ptrNovo} />
                      </td>
                      <td className="px-[18px] py-[13px] text-[12.5px]">{s.usuario.nome}</td>
                      <td className="px-[18px] py-[13px] text-[12.5px] text-texto-suave">
                        {s.revisor?.nome ?? '—'}
                      </td>
                      <td className="px-[18px] py-[13px]">
                        <Pill tom={STATUS[s.status].tom}>{STATUS[s.status].rotulo}</Pill>
                        {s.motivoRejeicao && (
                          <p className="mt-1 max-w-[220px] text-[11.5px] text-texto-suave">
                            {s.motivoRejeicao}
                          </p>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      )}

      {rejeitando && (
        <ModalRejeitar
          solicitacao={rejeitando}
          aoFechar={() => setRejeitando(null)}
          aoRejeitar={recarregar}
        />
      )}
    </div>
  );
}

function CartaoPendente({
  solicitacao: s,
  aoResolver,
  aoRejeitar,
}: {
  solicitacao: Solicitacao;
  aoResolver: () => void;
  aoRejeitar: () => void;
}) {
  const aprovar = useMutation({
    mutationFn: () => api.post(`/solicitacoes/${s.id}/aprovar`),
    onSuccess: aoResolver,
  });

  return (
    <article className="rounded-card border border-aviso/30 bg-superficie p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-mono text-[15px] font-bold">{s.ip}</p>
          <p className="text-[12.5px] text-texto-suave">
            {s.bloco?.cidr} · {s.bloco?.operadora.nome}
          </p>
        </div>
        <span className="flex shrink-0 items-center gap-1 text-[11.5px] text-texto-mudo">
          <Clock size={12} />
          {new Date(s.criadoEm).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}
        </span>
      </div>

      <div className="mt-4 rounded-chip bg-fundo p-3">
        <Alteracao de={s.ptrAntigo} para={s.ptrNovo} grande />
      </div>

      <p className="mt-3 text-[12.5px] text-texto-suave">
        Pedido por <strong className="font-semibold text-texto">{s.usuario.nome}</strong>
      </p>

      {aprovar.error && (
        <p className="mt-3 rounded-chip bg-erro-fundo px-3 py-2 text-[12.5px] font-semibold text-erro">
          {(aprovar.error as ApiError).mensagem}
        </p>
      )}

      <div className="mt-4 flex gap-2">
        <Botao
          variante="primario"
          className="flex-1"
          onClick={() => aprovar.mutate()}
          carregando={aprovar.isPending}
        >
          Aprovar e publicar
        </Botao>
        <Botao variante="perigo" onClick={aoRejeitar} disabled={aprovar.isPending}>
          Rejeitar
        </Botao>
      </div>
    </article>
  );
}

/** Mostra o antes e o depois lado a lado — é a informação central da revisão. */
function Alteracao({
  de,
  para,
  grande,
}: {
  de: string | null;
  para: string;
  grande?: boolean;
}) {
  const t = grande ? 'text-[13px]' : 'text-[12px]';
  return (
    <div className={`flex flex-wrap items-center gap-2 font-mono ${t}`}>
      <span className={de ? 'text-texto-suave line-through' : 'italic text-texto-mudo'}>
        {de ?? 'sem reverso'}
      </span>
      <ArrowRight size={13} className="shrink-0 text-texto-mudo" />
      <span className="font-semibold text-marca">{para}</span>
    </div>
  );
}

function ModalRejeitar({
  solicitacao: s,
  aoFechar,
  aoRejeitar,
}: {
  solicitacao: Solicitacao;
  aoFechar: () => void;
  aoRejeitar: () => void;
}) {
  const [motivo, setMotivo] = useState('');

  const rejeitar = useMutation({
    mutationFn: () => api.post(`/solicitacoes/${s.id}/rejeitar`, { motivo: motivo.trim() || undefined }),
    onSuccess: () => {
      aoRejeitar();
      aoFechar();
    },
  });

  return (
    <Modal
      aberto
      aoFechar={aoFechar}
      titulo="Rejeitar solicitação"
      descricao={`${s.ip} → ${s.ptrNovo}`}
      rodape={
        <>
          <Botao onClick={aoFechar}>Cancelar</Botao>
          <Botao variante="perigo" onClick={() => rejeitar.mutate()} carregando={rejeitar.isPending}>
            Rejeitar
          </Botao>
        </>
      }
    >
      <label className="block">
        <span className="mb-1.5 block text-[11px] font-bold uppercase tracking-[0.6px] text-texto-suave">
          Motivo
        </span>
        <textarea
          value={motivo}
          onChange={(e) => setMotivo(e.target.value)}
          rows={3}
          autoFocus
          placeholder="O nome pedido não pertence a um domínio da operadora."
          className="w-full rounded-controle border border-borda p-3 text-[13.5px] focus:border-marca focus:outline-none"
        />
        {/* O motivo aparece para o cliente no portal: sem ele, o pedido volta
            reprovado sem explicação e o cliente reabre igual. */}
        <span className="mt-1.5 block text-[12px] text-texto-suave">
          Opcional, mas recomendado — o cliente vê esta mensagem no portal.
        </span>
      </label>
    </Modal>
  );
}

function Esqueleto() {
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      {[0, 1].map((i) => (
        <div key={i} className="h-56 animate-pulse rounded-card border border-borda bg-superficie" />
      ))}
    </div>
  );
}
